import React, { useContext, useState, useEffect } from 'react';
import { API_BASE } from '../utils/apiBase';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { CartContext } from '../context/CartContext';
import { trackEvent } from './TrackingProvider';
import axios from 'axios';

const RESULT_KEYS = ['success', 'error', 'cancelled'];

const CheckoutForm = () => {
  const { cart, discount, clearCart } = useContext(CartContext);
  const [{ options }] = usePayPalScriptReducer();
  const navigate = useNavigate();

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
  });

  const [orderCreated, setOrderCreated] = useState(false);
  const [cmdIncrement, setCmdIncrement] = useState(null);
  const [orderID, setOrderId] = useState(null);
  // Résultat du paiement : null (en attente) | 'success' | 'error' | 'cancelled'
  const [paymentResult, setPaymentResult] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);
  // Montant et e-mail figés au moment du paiement (le panier est vidé après succès)
  const [paidSummary, setPaidSummary] = useState({ amount: '0.00', email: '' });

  const finishPayment = (result) => {
    setPaymentResult(result);
    setResultOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Développement uniquement : /checkout?demo_result=success|error|cancelled affiche la modale
  // sans passer par PayPal (code éliminé du build de production par NODE_ENV).
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const demo = new URLSearchParams(window.location.search).get('demo_result');
    if (demo && RESULT_KEYS.includes(demo)) {
      setCmdIncrement('WEB00000');
      setPaidSummary({ amount: '9.30', email: 'exemple@mail.fr' });
      setPaymentResult(demo);
      setResultOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryPayment = () => {
    setResultOpen(false);
    setPaymentResult(null);
  };

  // Tracker le démarrage du checkout
  useEffect(() => {
    trackEvent('checkout_start', {
      category: 'E-commerce',
      label: 'Checkout started',
      value: cart.reduce((sum, item) => sum + (item.price * item.quantity), 0) / 100
    });
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCustomerInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  // item.price est un montant TTC en centimes : la TVA est incluse, pas ajoutée
  const TVA_RATE = 0.2;
  const totalTTCBrut = cart.reduce((sum, item) => sum + (item.price * item.quantity) / 100, 0);
  const totalHT = (totalTTCBrut / (1 + TVA_RATE)).toFixed(2);
  const totalTVA = (totalTTCBrut - totalTTCBrut / (1 + TVA_RATE)).toFixed(2);
  const totalTTC = (totalTTCBrut - (parseFloat(discount) || 0)).toFixed(2);

  // Repli quand visuelSku manque : le nom de fichier d'un chemin /assets_react/... EST le SKU
  // config_carte.visuel (les anciens chemins Strapi n'existent plus depuis le 2026-09-17).
  const extractImageName = (imageUrl) => {
    const parts = String(imageUrl || '').split('/');
    return parts[parts.length - 1];
  };

  const prepareCardsForAPI = () => {
    return cart.map((item) => {
      // Mini-site de mariage (et futurs produits pilotés par option_schema) : objet distinct,
      // reconnu par apiEtsy.php::addOrder (branche additive product_type==='minisite') pour
      // écrire product_type + params dans cartes_v2 au lieu du chemin carte à gratter.
      if (item.productType) {
        return {
          client: customerInfo.name,
          product_type: item.productType,
          visuel: item.visuelSku || '',
          params: item.minisiteParams || {},
          dateCreation: new Date().toISOString().split('T')[0],
        };
      }

      return {
        client: customerInfo.name,
        visuel: item.visuelSku || extractImageName(item.selectedVisual),
        message: item.customText || 'Message par défaut',
        image: 'none',
        gratte: 'coeur',
        margin_top: 90,
        line_height: 45,
        text_margin_top: 1,
        theme: 'Carte à Gratter',
        // Titre visible personnalisable (ProductDetail.js) : vide si le visuel ne le propose
        // pas ou si le client n'a rien saisi — ScratchCard.js n'affiche rien dans ce cas.
        titre: item.customTitre || '',
        taille_titre: 30,
        couleur_text: '#000000',
        taille_text: 30,
        dateCreation: new Date().toISOString().split('T')[0],
        // Nombre de grattages autorisés (2 de base + options payantes achetées sur la fiche
        // produit, cf. ProductDetail.js) — lu par addOrder (apiEtsy.php) pour initialiser
        // cartes_use.max_scratch_allowed dès la création de la carte.
        max_scratch_allowed: item.maxScratchAllowed || 2,
      };
    });
  };

  const createOrderInAPI = async () => {
    try {
      const userResponse = await axios.get(`${API_BASE}/apiEtsy.php`, {
        params: {
          cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75',
          service: 'addUser',
          name: customerInfo.name,
          email: customerInfo.email,
          address: customerInfo.address,
          city: customerInfo.city,
          postal_code: customerInfo.postalCode,
          country: customerInfo.country,
        },
      });

      if (!userResponse.data.success || !userResponse.data.user_id) {
        throw new Error('Erreur de création d\'utilisateur');
      }

      const userId = userResponse.data.user_id;
      const cards = prepareCardsForAPI();

      const orderResponse = await axios.get(`${API_BASE}/apiEtsy.php`, {
        params: {
          cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75',
          service: 'addOrder',
          user_id: userId,
          total_ht: totalHT,
          total_tva: totalTVA,
          total_ttc: totalTTC,
          prov: 'site',
          payment_method: 'PayPal',
          cards: JSON.stringify(cards),
        },
      });

      if (!orderResponse.data.success || !orderResponse.data.cmd_increment) {
        throw new Error('Erreur de création de commande');
      }

      setCmdIncrement(orderResponse.data.cmd_increment);
      setOrderId(orderResponse.data.order_id);
      setOrderCreated(true);

      return { cmdIncrement: orderResponse.data.cmd_increment, orderID: orderResponse.data.order_id };
    } catch (error) {
      console.error('Erreur lors de la création de la commande ou des cartes:', error);
      return null;
    }
  };

  const createOrderInAPIWithoutPayment = async () => {
    const orderData = await createOrderInAPI();
  
    if (orderData && orderData.cmdIncrement && orderData.orderID) {
      try {
        const response = await axios.get(`${API_BASE}/apiEtsy.php`, {
          params: {
            cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75',
            service: 'addPayment',
            cmd_increment: orderData.cmdIncrement,
            order_id: orderData.orderID,
            payment_method: 'Gratuit',
            payment_status: 'completed',
            amount: totalTTC,
            transaction_id: '000000'
          }
        });
  
        // Traitement de la réponse en cas de réponse concaténée
        const responseText = response.data;
        const firstResponse = JSON.parse(responseText.split("}{")[0] + "}"); // Extrait uniquement le premier JSON
  
        if (firstResponse && firstResponse.success) {
          // Commande gratuite (code promo 100 %) : même événement « purchase » que le chemin PayPal,
          // pour que la commande soit rattachée à la visite dans « Tunnel d'acquisition ».
          trackEvent('purchase', {
            category: 'E-commerce',
            label: 'Order completed (free)',
            value: totalTTC,
            order_id: orderData.orderID
          });
          setPaidSummary({ amount: '0.00', email: customerInfo.email });
          clearCart();
          finishPayment('success');
        } else {
          console.error('Erreur lors de l\'enregistrement du paiement gratuit:', firstResponse.message);
          finishPayment('error');
        }
      } catch (error) {
        console.error('Erreur lors de l\'appel API pour le paiement gratuit:', error);
        finishPayment('error');
      }
    } else {
      console.error("Impossible d'ajouter le paiement gratuit, les identifiants de commande sont manquants.");
      finishPayment('error');
    }
  };
  
  
  
  

  const createOrder = async (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            currency_code: options.currency || 'EUR',
            value: totalTTC,
          },
          custom_id: cmdIncrement,
        },
      ],
    });
  };

  const onApprove = (data, actions) => {
    return actions.order.capture().then((details) => {
      // Tracker l'achat complété
      trackEvent('purchase', {
        category: 'E-commerce',
        label: 'Order completed',
        value: totalTTC,
        order_id: orderID,
        gtmDone: true // le dataLayer.push « purchase » détaillé est fait juste en dessous : pas de doublon GTM
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: orderID || details.id,
          currency: 'EUR',
          value: totalTTC,
          tax: totalTVA,
          shipping: 0,
          items: cart.map((item) => ({
            item_name: item.name,
            item_id: item.id,
            price: item.price / 100,
            quantity: item.quantity,
          })),
        },
      });

      setPaidSummary({ amount: totalTTC, email: customerInfo.email });
      clearCart();
      finishPayment('success');

      axios.get(`${API_BASE}/apiEtsy.php`, {
        params: {
          cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75',
          service: 'addPayment',
          cmd_increment: cmdIncrement,
          order_id: orderID,
          payment_method: 'PayPal',
          payment_status: 'completed',
          amount: totalTTC,
          transaction_id: details.id,
        },
      })
        .then((response) => {
          if (response.data.success) {
            console.log('Paiement et statut de la commande mis à jour');
          } else {
            console.error('Erreur lors de la mise à jour du paiement et du statut');
          }
        })
        .catch((error) => {
          console.error('Erreur lors de l\'appel API:', error);
        });

    }).catch((error) => {
      console.error('Capture PayPal échouée :', error);
      finishPayment('error');
    });
  };

  // Fenêtre PayPal fermée avant la fin : rien n'a été débité
  const onCancel = () => finishPayment('cancelled');
  // Erreur technique PayPal (carte refusée, script, réseau…)
  const onError = (err) => {
    console.error('Erreur PayPal :', err);
    finishPayment('error');
  };

  const RESULT_UI = {
    success: {
      severity: 'success',
      Icon: CheckCircleOutlineIcon,
      title: 'Paiement confirmé, merci pour votre achat !',
      body: (
        <>
          Votre paiement de <strong>{paidSummary.amount.replace('.', ',')} €</strong> a bien été reçu
          {cmdIncrement ? <> (commande n° <strong>{cmdIncrement}</strong>)</> : null}.
          Votre e-mail de confirmation, avec vos liens (carte à gratter, éditeur de faire-part), arrive à <strong>{paidSummary.email || 'votre adresse'}</strong> dans
          quelques minutes. Pensez à vérifier vos courriers indésirables.
        </>
      ),
    },
    error: {
      severity: 'error',
      Icon: ErrorOutlineIcon,
      title: 'Paiement refusé',
      body: (
        <>
          Votre paiement n'a pas pu être validé et <strong>aucun montant n'a été débité</strong>.
          Vous pouvez réessayer, ou choisir un autre moyen de paiement dans la fenêtre PayPal.
        </>
      ),
    },
    cancelled: {
      severity: 'warning',
      Icon: WarningAmberIcon,
      title: 'Paiement annulé',
      body: (
        <>
          La fenêtre PayPal a été fermée avant la fin du paiement. <strong>Aucun montant n'a été débité</strong>,
          votre commande est en attente : vous pouvez reprendre le paiement quand vous voulez.
        </>
      ),
    },
  };

  const renderResultBanner = () => {
    if (!paymentResult) return null;
    const ui = RESULT_UI[paymentResult];
    return (
      <Alert
        severity={ui.severity}
        icon={<ui.Icon fontSize="large" />}
        sx={{ mb: 3, alignItems: 'center', fontSize: '1.05rem', '& .MuiAlert-message': { width: '100%' } }}
        action={
          paymentResult === 'success' ? (
            <Button color="inherit" size="small" onClick={() => navigate('/')}>Accueil</Button>
          ) : (
            <Button color="inherit" size="small" onClick={retryPayment}>Réessayer</Button>
          )
        }
      >
        <AlertTitle sx={{ fontWeight: 700 }}>{ui.title}</AlertTitle>
        {ui.body}
      </Alert>
    );
  };

  const renderResultDialog = () => {
    if (!paymentResult) return null;
    const ui = RESULT_UI[paymentResult];
    const color = { success: '#2e7d32', error: '#d32f2f', warning: '#ed6c02' }[ui.severity];
    return (
      <Dialog open={resultOpen} onClose={() => setResultOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
          <ui.Icon sx={{ fontSize: 72, color, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color, mb: 1.5 }}>{ui.title}</Typography>
          <Typography variant="body1" sx={{ color: '#3a2b2b', lineHeight: 1.6 }}>{ui.body}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          {paymentResult === 'success' ? (
            <>
              <Button variant="outlined" onClick={() => setResultOpen(false)}>Fermer</Button>
              <Button variant="contained" onClick={() => navigate('/')}>Retour à l'accueil</Button>
            </>
          ) : (
            <>
              <Button variant="outlined" onClick={() => setResultOpen(false)}>Fermer</Button>
              <Button variant="contained" onClick={retryPayment}>Réessayer le paiement</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  if (paymentResult === 'success') {
    return (
      <Box sx={{ maxWidth: 760, mx: 'auto', py: 3 }}>
        {renderResultBanner()}
        {renderResultDialog()}
      </Box>
    );
  }

  if (cart.length === 0) {
    return <Typography variant="h5">Votre panier est vide.</Typography>;
  }

  return (
    <div>
      {renderResultBanner()}
      {renderResultDialog()}
      <Typography variant="h6" gutterBottom>
        Informations client
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="name"
            name="name"
            label="Nom"
            fullWidth
            autoComplete="given-name"
            variant="outlined"
            value={customerInfo.name}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="email"
            name="email"
            label="E-mail"
            fullWidth
            autoComplete="email"
            variant="outlined"
            value={customerInfo.email}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="address"
            name="address"
            label="Adresse"
            fullWidth
            autoComplete="shipping address-line1"
            variant="outlined"
            value={customerInfo.address}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="city"
            name="city"
            label="Ville"
            fullWidth
            autoComplete="shipping address-level2"
            variant="outlined"
            value={customerInfo.city}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="postalCode"
            name="postalCode"
            label="Code postal"
            fullWidth
            autoComplete="shipping postal-code"
            variant="outlined"
            value={customerInfo.postalCode}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="country"
            name="country"
            label="Pays"
            fullWidth
            autoComplete="shipping country"
            variant="outlined"
            value={customerInfo.country}
            onChange={handleInputChange}
          />
        </Grid>

        {!orderCreated && (
          <Grid item xs={12}>
            {totalTTC === '0.00' || totalTTC === '-0.00' ? (
              <Button variant="contained" color="primary" onClick={createOrderInAPIWithoutPayment}>
                Valider la commande
              </Button>
            ) : (
              <Button variant="contained" color="primary" onClick={createOrderInAPI}>
                Commander et payer
              </Button>
            )}
          </Grid>
        )}
      </Grid>

      <div style={{ marginTop: '20px' }}>
        <Typography variant="h6" gutterBottom>
          Récapitulatif du montant
        </Typography>
        <Typography variant="body1">Total HT : {totalHT} €</Typography>
        <Typography variant="body1">TVA (20 %) : {totalTVA} €</Typography>
        <Typography variant="body1">Réduction : {discount} €</Typography>
        <Typography variant="body1">Total TTC : {totalTTC} €</Typography>
      </div>

      {/* Boutons PayPal : après création de la commande, tant que le paiement n'est pas confirmé.
          La fenêtre de connexion PayPal elle-même est ouverte et dimensionnée par PayPal. */}
      {orderCreated && paymentResult !== 'success' && parseFloat(totalTTC) > 0 && (
        <Box sx={{ marginTop: '24px', maxWidth: 520 }}>
          <Typography variant="h6" gutterBottom>
            Paiement avec PayPal
          </Typography>
          <PayPalButtons
            createOrder={createOrder}
            onApprove={onApprove}
            onCancel={onCancel}
            onError={onError}
            forceReRender={[totalTTC, paymentResult]}
            style={{ layout: 'vertical', shape: 'rect', height: 55, label: 'pay' }}
          />
        </Box>
      )}
    </div>
  );
};

export default CheckoutForm;
